/**
 * src/components/layout/Sidebar.jsx
 * v1.0.0 — dual layout: 'grouped' (accordion) + 'rail' (Teams-style icon rail + mini panel)
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import AppLogo from '../shared/AppLogo.jsx';
import { useBranding } from '../../hooks/useBranding.js';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { isProspect, getModulePermissions, resolveSeasuiteRole } from '../../utils/auth.js';
import {
  useActionCount,
  formatActionBadge,
  ACTION_BADGE_INCOMPLETE_TITLE,
} from '../../hooks/useActionCount.js';
import { useUpdateChecker } from '../../hooks/useUpdateChecker.js';
import { useNavTracking } from '../../hooks/useNavTracking.js';
import {
  NAV_GROUPS,
  SYSTEM_NAV,
  SYSTEM_GROUP,
  navModuleKey,
  navRoleAllowed,
  navReleaseAllowed,
} from '../../config/navConfig.js';
import { SHIELD_ICONS } from '../../config/shieldIcons.jsx';
import { useMsrPublish } from '../dashboard/controlPosture.js';
import { useReleasedFlags } from '../../utils/useReleaseFlag.js';

const COLLAPSED_WIDTH = 56;
const DEFAULT_WIDTH = 220;
const MIN_WIDTH = 160;
const MAX_WIDTH = 420;
const RAIL_W = 56;
const PANEL_W = 214;

// ── Shared nav item renderer ──────────────────────────────────────────────────

function NavItem({
  path,
  icon,
  label,
  small = false,
  collapsed = false,
  pinned,
  onTogglePin,
  unlicensed = false,
}) {
  const showPin = !!onTogglePin && !collapsed;

  if (unlicensed) {
    return (
      <div
        style={{ position: 'relative', marginBottom: 2 }}
        title={`${label} — not available on your current plan`}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 9,
            padding: collapsed ? '5px 0' : '4px 8px',
            borderRadius: 8,
            fontSize: small ? 13 : 14,
            fontWeight: 400,
            color: 'var(--fg3)',
            background: 'transparent',
            opacity: 0.42,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          <span
            style={{
              flexShrink: 0,
              width: 22,
              height: 22,
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--bg3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              lineHeight: 1,
            }}
          >
            {icon}
          </span>
          {!collapsed && (
            <>
              <span style={{ flex: 1 }}>{label}</span>
              <span
                style={{
                  fontSize: 9,
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--fg3)',
                  letterSpacing: '0.06em',
                  flexShrink: 0,
                }}
              >
                UPGRADE
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={showPin ? 'nav-item-pinnable' : undefined} style={{ position: 'relative' }}>
      <NavLink
        to={path}
        title={collapsed ? label : undefined}
        style={{ display: 'block', textDecoration: 'none', marginBottom: 2 }}
      >
        {({ isActive }) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 9,
              padding: collapsed ? '5px 0' : '4px 8px',
              paddingRight: showPin ? 26 : undefined,
              borderRadius: 8,
              fontSize: small ? 13 : 14,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? 'var(--accent)' : small ? 'var(--fg3)' : 'var(--fg2)',
              background: isActive ? 'rgba(45,212,180,0.08)' : 'transparent',
              transition: 'all 0.15s',
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: isActive ? 28 : 22,
                height: isActive ? 28 : 22,
                borderRadius: isActive ? 8 : 6,
                border: isActive ? '1.5px solid rgba(45,212,180,0.55)' : '1px solid var(--border)',
                background: isActive ? 'rgba(45,212,180,0.15)' : 'var(--bg3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isActive ? 14 : 12,
                lineHeight: 1,
                transition: 'all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
            >
              {icon}
            </span>
            {!collapsed && label}
          </div>
        )}
      </NavLink>
      {showPin && (
        <button
          className={`pin-btn${pinned ? ' is-pinned' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onTogglePin();
          }}
          title={pinned ? 'Unpin from My Menu' : 'Pin to My Menu'}
          style={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            padding: '2px 5px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 9,
            lineHeight: 1,
            color: pinned ? 'var(--accent)' : 'var(--fg3)',
            transition: 'color 0.15s, opacity 0.15s',
          }}
        >
          {pinned ? '◆' : '◇'}
        </button>
      )}
    </div>
  );
}

function GroupLabel({ label, collapsed, isOpen, onToggle }) {
  if (collapsed) return null;
  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '6px 10px',
        marginTop: 4,
        marginBottom: 2,
        background: isOpen ? 'rgba(45,212,180,0.12)' : 'rgba(255,255,255,0.04)',
        border: '1px solid',
        borderColor: isOpen ? 'rgba(45,212,180,0.30)' : 'rgba(255,255,255,0.08)',
        borderRadius: 6,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(45,212,180,0.15)';
        e.currentTarget.style.borderColor = 'rgba(45,212,180,0.35)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isOpen
          ? 'rgba(45,212,180,0.12)'
          : 'rgba(255,255,255,0.04)';
        e.currentTarget.style.borderColor = isOpen
          ? 'rgba(45,212,180,0.30)'
          : 'rgba(255,255,255,0.08)';
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: isOpen ? 'var(--accent)' : 'rgba(255,255,255,0.60)',
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.10em',
          transition: 'color 0.15s',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 9,
          color: isOpen ? 'var(--accent)' : 'rgba(255,255,255,0.35)',
          transition: 'transform 0.2s, color 0.15s',
          transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
          lineHeight: 1,
        }}
      >
        ▼
      </span>
    </button>
  );
}

// ── Sub-group tab strip ───────────────────────────────────────────────────────

function SubGroupTabs({ subGroups, activeKey, onSelect }) {
  return (
    <div style={{ display: 'flex', gap: 3, padding: '0 2px 5px' }}>
      {subGroups.map((sg) => {
        const active = sg.key === activeKey;
        return (
          <button
            key={sg.key}
            onClick={() => onSelect(sg.key)}
            style={{
              flex: 1,
              padding: '4px 6px',
              borderRadius: 6,
              border: '1px solid',
              borderColor: active ? 'rgba(45,212,180,0.4)' : 'rgba(255,255,255,0.08)',
              background: active ? 'rgba(45,212,180,0.10)' : 'rgba(255,255,255,0.02)',
              fontSize: 10,
              fontWeight: active ? 600 : 400,
              color: active ? 'var(--accent)' : 'var(--fg3)',
              cursor: 'pointer',
              transition: 'all 0.15s',
              fontFamily: 'var(--font-mono)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              letterSpacing: '0.02em',
            }}
            onMouseEnter={(e) => {
              if (!active) {
                e.currentTarget.style.borderColor = 'rgba(45,212,180,0.2)';
                e.currentTarget.style.color = 'var(--fg2)';
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.color = 'var(--fg3)';
              }
            }}
          >
            {sg.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Helper: hex colour + alpha → rgba string ──────────────────────────────────

function hexAlpha(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── Rail: coloured icon button for a group ────────────────────────────────────

function RailGroupButton({ label, icon, color, groupKey, isActive, hasActiveRoute, onClick }) {
  const shieldIcon = SHIELD_ICONS[groupKey];
  return (
    <button
      title={label}
      onClick={onClick}
      style={{
        width: '100%',
        padding: '9px 0 7px',
        background: isActive ? hexAlpha(color, 0.1) : 'transparent',
        border: 'none',
        borderLeft: `2.5px solid ${isActive ? color : 'transparent'}`,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 5,
        transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.background = hexAlpha(color, 0.06);
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.background = 'transparent';
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: isActive
            ? hexAlpha(color, 0.18)
            : hasActiveRoute
              ? hexAlpha(color, 0.1)
              : hexAlpha(color, 0.07),
          border: `1.5px solid ${isActive ? hexAlpha(color, 0.65) : hasActiveRoute ? hexAlpha(color, 0.4) : hexAlpha(color, 0.25)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
          transform: isActive ? 'scale(1.10)' : 'scale(1)',
        }}
      >
        <span
          style={{
            fontSize: 17,
            lineHeight: 1,
            color: isActive
              ? color
              : hasActiveRoute
                ? hexAlpha(color, 0.85)
                : hexAlpha(color, 0.55),
            transition: 'color 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {shieldIcon ?? icon}
        </span>
      </div>
      <span
        style={{
          fontSize: 8,
          fontFamily: 'var(--font-mono)',
          color: isActive ? color : hasActiveRoute ? 'var(--fg2)' : 'var(--fg3)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          lineHeight: 1,
          transition: 'color 0.15s',
        }}
      >
        {label.split(' ')[0].slice(0, 5)}
      </span>
    </button>
  );
}

// ── Layout toggle (grouped ↔ rail) ────────────────────────────────────────────

function LayoutToggle({ currentStyle, onSwitch }) {
  const toRail = currentStyle !== 'rail';
  return (
    <button
      title={toRail ? 'Switch to compact rail view' : 'Switch to full sidebar'}
      onClick={() => onSwitch(toRail ? 'rail' : 'grouped')}
      style={{
        padding: '5px 7px',
        background: 'var(--bg3)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        fontSize: 12,
        color: 'var(--fg3)',
        cursor: 'pointer',
        lineHeight: 1,
        flexShrink: 0,
        transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(45,212,180,0.3)';
        e.currentTarget.style.color = 'var(--accent)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.color = 'var(--fg3)';
      }}
    >
      {toRail ? '⊟' : '⊞'}
    </button>
  );
}

// ── My Menu: sub-section divider (tiny label between most-used / recent) ─────

function SmartDivider({ label }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px 2px',
        margin: '2px 0',
      }}
    >
      <span
        style={{
          fontSize: 8,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: 'var(--fg3)',
          opacity: 0.6,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Sidebar({
  session,
  onLogout,
  onUserSettings,
  pushModal,
  canAdmin: canAdminProp,
  state,
  tenant,
  appVersion,
  users,
  persistUsers,
  planGating,
  obligationTasks,
  // GH #7057: derived by App from the user's own record (hrRecruitmentAccess).
  canHR = false,
}) {
  const role = session?.role || 'reader';
  const prospect = isProspect(role);
  const location = useLocation();
  const navigate = useNavigate();
  // msr 4.10a: the Improvement entry follows the server's publish switch.
  const msrPublish = useMsrPublish();
  // RPDA-04: release flags the server resolved ON (fail-closed while loading).
  const releasedFlags = useReleasedFlags();

  const resolvedBranding = useBranding(state, tenant);
  const tenantLogo = resolvedBranding.logoImage || null;
  const logoZoom = resolvedBranding.zoom ?? 1;
  const logoPanX = resolvedBranding.panX ?? 0;
  const logoPanY = resolvedBranding.panY ?? 0;

  // ── Read user-persisted sidebar prefs ──
  const currentUser = users?.find((u) => u.id === session?.userId);
  const userSidebarPrefs = currentUser?.sidebarPrefs || {};

  const openChangelog = () => pushModal({ type: 'changelog' });
  const [collapsed, setCollapsed] = useState(() => {
    if (userSidebarPrefs.collapsed != null) return userSidebarPrefs.collapsed;
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const fromUser = userSidebarPrefs.width;
    if (fromUser && !isNaN(fromUser)) return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, fromUser));
    const saved = parseInt(localStorage.getItem('sidebar-width'), 10);
    return isNaN(saved) ? DEFAULT_WIDTH : Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, saved));
  });
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    // Try user prefs first (from database)
    if (userSidebarPrefs.collapsedGroups) return userSidebarPrefs.collapsedGroups;
    // Fall back to localStorage (survives hard refresh before user data loads)
    const stored = localStorage.getItem('sidebar-collapsed-groups');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (_e) {
        // Ignore parse errors, fall through to defaults
      }
    }
    // No stored prefs — default to only the route-matching group open
    const allKeys = ['my-menu', ...NAV_GROUPS.map((g) => g.key), 'system', 'admin'];
    const defaults = Object.fromEntries(allKeys.map((k) => [k, true])); // all closed
    defaults['my-menu'] = false; // My Menu open by default
    const activeGroup = NAV_GROUPS.find((group) => {
      const items = group.subGroups ? group.subGroups.flatMap((sg) => sg.items) : group.items;
      return items.some((item) => location.pathname.startsWith(item.path));
    });
    defaults[(activeGroup || NAV_GROUPS[0]).key] = false; // open active or Work
    return defaults;
  });
  // Tracks which sub-group tab is manually selected per group key
  const [activeSubGroups, setActiveSubGroups] = useState(() => {
    const stored = localStorage.getItem('sidebar-active-subgroups');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (_e) {
        return {};
      }
    }
    return {};
  });
  const [isDragging, setIsDragging] = useState(false);

  // ── Rail / grouped layout preference ──
  const [sidebarStyle, setSidebarStyle] = useState(() => {
    if (userSidebarPrefs.sidebarStyle) return userSidebarPrefs.sidebarStyle;
    return localStorage.getItem('sidebar-style') || 'rail';
  });
  // Which groups' mini-panel sections are open in rail mode (toggle, multi-select)
  const [railActiveGroups, setRailActiveGroupsRaw] = useState(() => {
    // Restore from persisted prefs if available (from database)
    if (Array.isArray(userSidebarPrefs.railActiveGroups)) {
      return new Set(userSidebarPrefs.railActiveGroups);
    }
    // Fall back to localStorage (survives hard refresh before user data loads)
    const stored = localStorage.getItem('sidebar-rail-active-groups');
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch (_e) {
        // Ignore parse errors, fall through to defaults
      }
    }
    const active = NAV_GROUPS.find((g) => {
      const items = g.subGroups ? g.subGroups.flatMap((sg) => sg.items) : g.items;
      return items.some((i) => location.pathname.startsWith(i.path));
    });
    return new Set(active ? [active.key] : [NAV_GROUPS[0].key]);
  });

  const startXRef = useRef(0);
  const startWidthRef = useRef(sidebarWidth);
  const sidebarWidthRef = useRef(sidebarWidth);

  useEffect(() => {
    sidebarWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  // ── Persist sidebar prefs to user object ──
  // Use a ref so the debounced timeout always reads the latest users array,
  // avoiding a stale-closure race where users=[] is captured before the
  // async loadTenantUsers resolves, which would wipe all users from the DB.
  const usersRef = useRef(users);
  useEffect(() => {
    usersRef.current = users;
  }, [users]);
  const persistTimeoutRef = useRef(null);
  const persistSidebarPrefs = useCallback(
    (prefs) => {
      if (!persistUsers || !users?.length || !session?.userId) return false;
      clearTimeout(persistTimeoutRef.current);
      persistTimeoutRef.current = setTimeout(() => {
        const latestUsers = usersRef.current;
        if (!latestUsers?.length) return;
        const updated = latestUsers.map((u) =>
          u.id === session.userId ? { ...u, sidebarPrefs: { ...u.sidebarPrefs, ...prefs } } : u
        );
        persistUsers(updated);
      }, 800);
      return true;
    },
    [persistUsers, users, session?.userId]
  );

  // Wrapper: persist railActiveGroups as array whenever it changes
  const setRailActiveGroups = useCallback(
    (updater) => {
      setRailActiveGroupsRaw((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (next === prev) return prev;
        persistSidebarPrefs({ railActiveGroups: [...next] });
        return next;
      });
    },
    [persistSidebarPrefs]
  );

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed));
  }, [collapsed]);
  useEffect(() => {
    localStorage.setItem('sidebar-width', String(sidebarWidth));
  }, [sidebarWidth]);
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed-groups', JSON.stringify(collapsedGroups));
  }, [collapsedGroups]);
  useEffect(() => {
    localStorage.setItem('sidebar-rail-active-groups', JSON.stringify([...railActiveGroups]));
  }, [railActiveGroups]);
  useEffect(() => {
    localStorage.setItem('sidebar-style', sidebarStyle);
  }, [sidebarStyle]);
  useEffect(() => {
    localStorage.setItem('sidebar-active-subgroups', JSON.stringify(activeSubGroups));
  }, [activeSubGroups]);

  // Sync rail active groups when route changes (e.g. browser back/forward)
  useEffect(() => {
    if (sidebarStyle !== 'rail') return;
    const active = NAV_GROUPS.find((g) => {
      const items = g.subGroups ? g.subGroups.flatMap((sg) => sg.items) : g.items;
      return items.some((i) => location.pathname.startsWith(i.path));
    });
    if (active) {
      setRailActiveGroups((prev) => {
        if (prev.has(active.key)) return prev;
        const next = new Set(prev);
        next.add(active.key);
        return next;
      });
    }
  }, [location.pathname, sidebarStyle]);

  const handleSetSidebarStyle = useCallback(
    (style) => {
      setSidebarStyle(style);
      persistSidebarPrefs({ sidebarStyle: style });
    },
    [persistSidebarPrefs]
  );

  const isGroupOpen = (key) => !collapsedGroups[key];
  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const isCurrentlyOpen = !prev[key];
      let next;
      if (isCurrentlyOpen) {
        // Close this group (all closed)
        next = { ...prev, [key]: true };
      } else {
        // Open this group, close all others
        const allKeys = ['my-menu', ...NAV_GROUPS.map((g) => g.key), 'system', 'admin'];
        next = Object.fromEntries(allKeys.map((k) => [k, true]));
        next[key] = false;
      }
      persistSidebarPrefs({ collapsedGroups: next });
      return next;
    });
  };

  const handleCollapse = useCallback(
    (val) => {
      setCollapsed(val);
      persistSidebarPrefs({ collapsed: val });
    },
    [persistSidebarPrefs]
  );

  const handleResizeMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = sidebarWidthRef.current;
      setIsDragging(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const onMove = (ev) => {
        const next = Math.max(
          MIN_WIDTH,
          Math.min(MAX_WIDTH, startWidthRef.current + (ev.clientX - startXRef.current))
        );
        setSidebarWidth(next);
      };
      const onUp = () => {
        setIsDragging(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        persistSidebarPrefs({ width: sidebarWidthRef.current });
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [persistSidebarPrefs]
  );

  const seasuiteRole = resolveSeasuiteRole(session?.userId, users, state?.seasuiteRoles);
  const modulePerm = getModulePermissions(session, seasuiteRole);

  // Returns the active sub-group key for a group, driven by current route first,
  // then manual selection, then default to first sub-group.
  const getActiveSubGroupKey = useCallback(
    (group) => {
      if (!group.subGroups) return null;
      for (const sg of group.subGroups) {
        if (
          sg.items.some(
            (item) =>
              location.pathname === item.path || location.pathname.startsWith(item.path + '/')
          )
        ) {
          return sg.key;
        }
      }
      return activeSubGroups[group.key] || group.subGroups[0].key;
    },
    [location.pathname, activeSubGroups]
  );

  // PROD-B-002 — the Products item is only shown when the CRA framework is
  // enabled for the tenant. Read from state, never from a hard-coded flag.
  const craEnabled = (state?.frameworks || []).find((f) => f.id === 'fw-cra')?.enabled === true;

  // CUS-B-002 — the Delivery Chain item (Customers → Delivery Agreements →
  // Subcontractors, one tabbed page under the 'customers' key) is only shown
  // when the tenant has the SKI-0215 (Supplier Risk Assessment) framework.
  // Read from state. The 'subcontractors' key is kept in the test below
  // because the /subcontractors route still exists and still guards on it.
  const skiEnabled = (state?.frameworks || []).some((f) => f.id === 'fw-ski-0215');

  // User preference: show plan-gated items grayed out so users can see what exists
  // in the product without being able to click them. Default: true.
  const showUnlicensed = userSidebarPrefs?.showUnlicensedFeatures ?? true;

  const filterItems = useCallback(
    (items) =>
      items
        .filter((item) => {
          if (modulePerm[navModuleKey(item)]?.access === false) return false;
          if (item.hrOnly && !canHR) return false;
          if (!navReleaseAllowed(item, releasedFlags)) return false;
          if (item.key === 'products' && !craEnabled) return false;
          if ((item.key === 'customers' || item.key === 'subcontractors') && !skiEnabled)
            return false;
          const isUnlicensed = planGating && !planGating.isModuleAllowed(navModuleKey(item));
          if (isUnlicensed && !showUnlicensed) return false;
          return true;
        })
        .map((item) => {
          const isUnlicensed = planGating && !planGating.isModuleAllowed(navModuleKey(item));
          return isUnlicensed ? { ...item, unlicensed: true } : item;
        }),
    [modulePerm, planGating, craEnabled, skiEnabled, showUnlicensed, canHR, releasedFlags]
  );

  // ── Navigation tracking + My Menu ──
  const { smartMenu, togglePin, isPinned } = useNavTracking(
    userSidebarPrefs,
    persistSidebarPrefs,
    filterItems
  );
  const hasMyMenu =
    smartMenu.pinned.length > 0 || smartMenu.mostUsed.length > 0 || smartMenu.recent.length > 0;

  const { actionCount, actionCountIncomplete, hasDocumentApprovals } = useActionCount(
    state,
    session,
    users,
    { obligationTasks, tenant }
  );
  // GH #8721: a failed read shows "n+" / "—", never a bare lower count.
  const actionBadge = formatActionBadge(actionCount, actionCountIncomplete);
  const actionBadgeTitle = actionCountIncomplete ? ACTION_BADGE_INCOMPLETE_TITLE : undefined;
  const { hasUpdates, pendingCount, env: updateEnv, checked: updateChecked } = useUpdateChecker();

  const displayVersion = appVersion ?? '…';
  const currentWidth = collapsed ? COLLAPSED_WIDTH : sidebarWidth;

  // ── Rail layout ─────────────────────────────────────────────────────────────
  if (sidebarStyle === 'rail' && !prospect) {
    const systemActive = railActiveGroups.has('system');
    const myMenuActive = railActiveGroups.has('my-menu');
    const panelOpen = railActiveGroups.size > 0;
    const totalW = RAIL_W + (panelOpen ? PANEL_W : 0);

    const allSystemItems = [
      ...SYSTEM_NAV.filter((item) =>
        navRoleAllowed(item, { canAdmin: canAdminProp, role, msrPublish, releasedFlags })
      )
        .filter((item) => {
          const isUnlicensed = planGating && !planGating.isModuleAllowed(navModuleKey(item));
          return !isUnlicensed || showUnlicensed;
        })
        .map((item) => {
          const isUnlicensed = planGating && !planGating.isModuleAllowed(navModuleKey(item));
          return isUnlicensed ? { ...item, unlicensed: true } : item;
        }),
      { path: '/settings', icon: 'ᛋ', label: 'Settings', key: 'settings' },
    ];

    const MY_MENU_GROUP = { key: 'my-menu', label: 'My Menu', icon: '★', color: '#2dd4b4' };

    // Build ordered list of active groups for the panel
    const activePanelSections = [];
    // My Menu comes first when active
    if (myMenuActive) {
      activePanelSections.push({ group: MY_MENU_GROUP, items: null, isMyMenu: true });
    }
    for (const group of NAV_GROUPS) {
      if (!railActiveGroups.has(group.key)) continue;
      const items = group.subGroups
        ? group.subGroups.flatMap((sg) => filterItems(sg.items))
        : filterItems(group.items);
      if (items.length > 0) activePanelSections.push({ group, items });
    }
    if (systemActive) {
      activePanelSections.push({ group: SYSTEM_GROUP, items: allSystemItems });
    }

    return (
      <aside
        className="sidebar"
        style={{
          width: totalW,
          minWidth: totalW,
          maxWidth: totalW,
          display: 'flex',
          flexDirection: 'row',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <style>{`
          @keyframes sidebarPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%       { opacity: 0.5; transform: scale(0.85); }
          }
        `}</style>

        {/* ── Icon rail ── */}
        <div
          style={{
            width: RAIL_W,
            minWidth: RAIL_W,
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid var(--border)',
            height: '100%',
          }}
        >
          {/* Brand */}
          <div
            style={{
              padding: '14px 0 12px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            {tenantLogo ? (
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  overflow: 'hidden',
                  background: 'var(--bg2)',
                  position: 'relative',
                }}
              >
                <img
                  src={tenantLogo}
                  alt="Logo"
                  draggable={false}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(calc(-50% + ${logoPanX * (32 / 120)}px), calc(-50% + ${logoPanY * (32 / 120)}px)) scale(${logoZoom})`,
                    width: 32,
                    height: 32,
                    objectFit: 'contain',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            ) : (
              <AppLogo size={32} borderRadius={9} />
            )}
            {updateChecked && hasUpdates && (
              <div
                title={`${pendingCount} update${pendingCount !== 1 ? 's' : ''} incoming`}
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 8,
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: updateEnv === 'test' ? '#fbbf24' : 'var(--accent)',
                  border: '2px solid var(--bg)',
                  animation: 'sidebarPulse 2s ease-in-out infinite',
                  cursor: 'pointer',
                }}
                onClick={() => openChangelog()}
              />
            )}
          </div>

          {/* Group icons */}
          <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {/* My Menu rail button — always visible so pin is discoverable */}
            <RailGroupButton
              label="My Menu"
              icon="★"
              color="#2dd4b4"
              groupKey="my-menu"
              isActive={myMenuActive}
              hasActiveRoute={false}
              onClick={() => {
                setRailActiveGroups((prev) => {
                  const next = new Set(prev);
                  if (next.has('my-menu')) next.delete('my-menu');
                  else next.add('my-menu');
                  return next;
                });
              }}
            />
            <div style={{ height: 1, background: 'var(--border)', margin: '4px 10px' }} />
            {NAV_GROUPS.map((group) => {
              const allItems = group.subGroups
                ? group.subGroups.flatMap((sg) => sg.items)
                : group.items;
              const visible = group.subGroups
                ? group.subGroups.some((sg) => filterItems(sg.items).length > 0)
                : filterItems(group.items).length > 0;
              if (!visible) return null;
              const isActive = railActiveGroups.has(group.key);
              const hasActiveRoute = allItems.some((i) => location.pathname.startsWith(i.path));
              const firstItem = group.subGroups
                ? filterItems(group.subGroups.flatMap((sg) => sg.items))[0]
                : filterItems(allItems)[0];
              return (
                <RailGroupButton
                  key={group.key}
                  label={group.label}
                  icon={group.icon}
                  color={group.color}
                  groupKey={group.key}
                  isActive={isActive}
                  hasActiveRoute={!isActive && hasActiveRoute}
                  onClick={() => {
                    setRailActiveGroups((prev) => {
                      const next = new Set(prev);
                      if (next.has(group.key)) {
                        next.delete(group.key);
                      } else {
                        next.add(group.key);
                        if (firstItem) navigate(firstItem.path);
                      }
                      return next;
                    });
                  }}
                />
              );
            })}

            {/* Divider before system/admin */}
            <div style={{ height: 1, background: 'var(--border)', margin: '4px 10px' }} />

            <RailGroupButton
              label={SYSTEM_GROUP.label}
              icon={SYSTEM_GROUP.icon}
              color={SYSTEM_GROUP.color}
              groupKey={SYSTEM_GROUP.key}
              isActive={systemActive}
              hasActiveRoute={!systemActive && location.pathname === '/settings'}
              onClick={() => {
                setRailActiveGroups((prev) => {
                  const next = new Set(prev);
                  if (next.has('system')) {
                    next.delete('system');
                  } else {
                    next.add('system');
                    navigate('/integrations');
                  }
                  return next;
                });
              }}
            />
          </nav>

          {/* Rail footer: action centre + switch layout */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              padding: '10px 0 12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => pushModal({ type: 'action-centre' })}
              title="Action Centre"
              style={{
                position: 'relative',
                padding: '6px 8px',
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 7,
                fontSize: 14,
                color: hasDocumentApprovals ? '#facc15' : 'var(--fg3)',
                cursor: 'pointer',
                lineHeight: 1,
              }}
            >
              ◎
              {actionBadge && (
                <span
                  title={actionBadgeTitle}
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    minWidth: 15,
                    height: 15,
                    borderRadius: 8,
                    background: hasDocumentApprovals ? '#facc15' : 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 8,
                    fontWeight: 700,
                    color: '#0d1f1e',
                    padding: '0 3px',
                  }}
                >
                  {actionBadge}
                </span>
              )}
            </button>
            <button
              title="Switch to full sidebar"
              onClick={() => handleSetSidebarStyle('grouped')}
              style={{
                padding: '4px 7px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 11,
                color: 'var(--fg3)',
                cursor: 'pointer',
                lineHeight: 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(45,212,180,0.3)';
                e.currentTarget.style.color = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.color = 'var(--fg3)';
              }}
            >
              ⊞
            </button>
          </div>
        </div>

        {/* ── Mini panel (shows when any group is active) ── */}
        {panelOpen && (
          <div
            style={{
              width: PANEL_W,
              minWidth: PANEL_W,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Panel header */}
            <div
              style={{
                padding: '14px 12px 10px',
                borderBottom: '1px solid var(--border)',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: 'var(--accent)',
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.10em',
                }}
              >
                {resolvedBranding.name}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--fg3)',
                  marginTop: 2,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {session?.displayName || ''}
              </div>
            </div>

            {/* Stacked group sections */}
            <nav style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
              <style>{`
                .nav-item-pinnable .pin-btn { opacity: 0; pointer-events: none; }
                .nav-item-pinnable:hover .pin-btn { opacity: 0.5; pointer-events: auto; }
                .nav-item-pinnable:hover .pin-btn:hover { opacity: 1; }
                .nav-item-pinnable .pin-btn.is-pinned { opacity: 1; pointer-events: auto; }
              `}</style>
              {activePanelSections.map(({ group, items, isMyMenu }, idx) => (
                <div
                  key={group.key}
                  style={{
                    background: hexAlpha(group.color, 0.06),
                    borderBottom:
                      idx < activePanelSections.length - 1
                        ? `1px solid ${hexAlpha(group.color, 0.15)}`
                        : 'none',
                  }}
                >
                  {/* Group label header */}
                  <div
                    style={{
                      padding: '8px 10px 4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 14, lineHeight: 1, color: group.color }}>
                      {group.icon}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: group.color,
                        fontFamily: 'var(--font-mono)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.10em',
                      }}
                    >
                      {group.label}
                    </span>
                  </div>
                  {/* Group nav items */}
                  <div style={{ padding: '2px 6px 6px' }}>
                    {isMyMenu ? (
                      <>
                        {smartMenu.pinned.length > 0 && (
                          <>
                            <SmartDivider label="pinned" />
                            {smartMenu.pinned.map((item) => (
                              <NavItem
                                key={`rpin-${item.key}`}
                                {...item}
                                collapsed={false}
                                pinned
                                onTogglePin={() => togglePin(item.key)}
                              />
                            ))}
                          </>
                        )}
                        {smartMenu.mostUsed.length > 0 && (
                          <>
                            <SmartDivider label="frequent" />
                            {smartMenu.mostUsed.map((item) => (
                              <NavItem
                                key={`rfreq-${item.key}`}
                                {...item}
                                collapsed={false}
                                onTogglePin={() => togglePin(item.key)}
                                pinned={isPinned(item.key)}
                              />
                            ))}
                          </>
                        )}
                        {smartMenu.recent.length > 0 && (
                          <>
                            <SmartDivider label="recent" />
                            {smartMenu.recent.map((item) => (
                              <NavItem
                                key={`rrec-${item.key}`}
                                {...item}
                                collapsed={false}
                                onTogglePin={() => togglePin(item.key)}
                                pinned={isPinned(item.key)}
                              />
                            ))}
                          </>
                        )}
                        {!hasMyMenu && (
                          <div
                            style={{
                              padding: '8px 10px',
                              fontSize: 11,
                              color: 'var(--fg3)',
                              fontFamily: 'var(--font-mono)',
                              lineHeight: 1.4,
                            }}
                          >
                            Pin items with ◇ to build your menu. Frequent and recent pages appear
                            here automatically.
                          </div>
                        )}
                      </>
                    ) : (
                      items.map((item) => (
                        <NavItem
                          key={item.path}
                          {...item}
                          collapsed={false}
                          small={group.key === 'system' || group.key === 'admin'}
                          onTogglePin={() => togglePin(item.key)}
                          pinned={isPinned(item.key)}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))}
              {activePanelSections.length === 0 && (
                <div
                  style={{
                    padding: '20px 10px',
                    fontSize: 12,
                    color: 'var(--fg3)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  select a section
                </div>
              )}
            </nav>

            {/* Panel footer */}
            <div
              style={{
                borderTop: '1px solid var(--border)',
                padding: '10px 10px 12px',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', gap: 5 }}>
                <button
                  onClick={onUserSettings}
                  style={{
                    flex: 1,
                    padding: '5px 0',
                    background: 'var(--bg3)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    fontSize: 10,
                    color: 'var(--fg3)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  settings
                </button>
                <button
                  onClick={onLogout}
                  style={{
                    flex: 1,
                    padding: '5px 0',
                    background: 'var(--bg3)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    fontSize: 10,
                    color: 'var(--fg3)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  logout
                </button>
              </div>
              <button
                onClick={() => openChangelog()}
                style={{
                  marginTop: 6,
                  width: '100%',
                  padding: '4px 8px',
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(45,212,180,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                <span
                  style={{
                    fontSize: 9,
                    color: 'var(--fg3)',
                    fontFamily: 'var(--font-mono)',
                    letterSpacing: '0.05em',
                  }}
                >
                  VERSION
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 600,
                    color: 'var(--fg3)',
                  }}
                >
                  v{displayVersion}
                </span>
              </button>
            </div>
          </div>
        )}
      </aside>
    );
  }

  // ── Grouped layout (default) ─────────────────────────────────────────────────
  return (
    <aside
      className="sidebar"
      style={{
        width: currentWidth,
        minWidth: currentWidth,
        maxWidth: currentWidth,
        transition: isDragging
          ? 'none'
          : 'width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease',
        position: 'relative',
      }}
    >
      {/* ── Brand header ── */}
      <div
        style={{
          padding: collapsed ? '20px 0 16px' : '20px 18px 16px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        {collapsed ? (
          <div style={{ position: 'relative' }}>
            {tenantLogo ? (
              <div
                title="Expand sidebar"
                onClick={() => handleCollapse(false)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: 'var(--bg2)',
                  position: 'relative',
                }}
              >
                <img
                  src={tenantLogo}
                  alt="Logo"
                  draggable={false}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(calc(-50% + ${logoPanX * (34 / 120)}px), calc(-50% + ${logoPanY * (34 / 120)}px)) scale(${logoZoom})`,
                    width: 34,
                    height: 34,
                    objectFit: 'contain',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            ) : (
              <div
                title="Expand sidebar"
                onClick={() => handleCollapse(false)}
                style={{ cursor: 'pointer' }}
              >
                <AppLogo size={34} borderRadius={10} />
              </div>
            )}
            {updateChecked && hasUpdates && (
              <div
                title={`${pendingCount} update${pendingCount !== 1 ? 's' : ''} incoming`}
                onClick={() => window.open('/changelog.html', '_blank')}
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: updateEnv === 'test' ? '#fbbf24' : 'var(--accent)',
                  border: '2px solid var(--bg)',
                  cursor: 'pointer',
                  animation: 'sidebarPulse 2s ease-in-out infinite',
                }}
              />
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {tenantLogo ? (
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      overflow: 'hidden',
                      background: 'var(--bg2)',
                      position: 'relative',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={tenantLogo}
                      alt="Logo"
                      draggable={false}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: `translate(calc(-50% + ${logoPanX * (34 / 120)}px), calc(-50% + ${logoPanY * (34 / 120)}px)) scale(${logoZoom})`,
                        width: 34,
                        height: 34,
                        objectFit: 'contain',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                ) : (
                  <AppLogo size={34} borderRadius={10} />
                )}
                {updateChecked && hasUpdates && (
                  <div
                    title={`${pendingCount} update${pendingCount !== 1 ? 's' : ''} incoming — click to view changelog`}
                    onClick={() => window.open('/changelog.html', '_blank')}
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: 11,
                      height: 11,
                      borderRadius: '50%',
                      background: updateEnv === 'test' ? '#fbbf24' : 'var(--accent)',
                      border: '2px solid var(--bg)',
                      cursor: 'pointer',
                      animation: 'sidebarPulse 2s ease-in-out infinite',
                    }}
                  />
                )}
              </div>
              <div>
                <div
                  className="display"
                  style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg)', lineHeight: 1.2 }}
                >
                  {resolvedBranding.name}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--fg3)',
                    marginTop: 1,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {prospect ? 'Prospect' : session?.displayName || ''}
                </div>
              </div>
            </div>
          </>
        )}
        <style>{`
          @keyframes sidebarPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%       { opacity: 0.5; transform: scale(0.85); }
          }
          .nav-item-pinnable .pin-btn { opacity: 0; pointer-events: none; }
          .nav-item-pinnable:hover .pin-btn { opacity: 0.5; pointer-events: auto; }
          .nav-item-pinnable:hover .pin-btn:hover { opacity: 1; }
          .nav-item-pinnable .pin-btn.is-pinned { opacity: 1; pointer-events: auto; }
        `}</style>
      </div>

      {/* ── Navigation ── */}
      <nav style={{ flex: 1, padding: collapsed ? '10px 4px' : '10px 8px', overflowY: 'auto' }}>
        {prospect ? (
          <NavItem path="/gap" icon="ᚨ" label="Gap Analysis" collapsed={collapsed} />
        ) : (
          <>
            {/* ── My Menu (pinned + most-used + recent) ── */}
            {hasMyMenu && !collapsed && (
              <div style={{ marginBottom: 4 }}>
                <GroupLabel
                  label="My Menu"
                  collapsed={false}
                  isOpen={isGroupOpen('my-menu')}
                  onToggle={() => toggleGroup('my-menu')}
                />
                <div
                  style={{
                    overflow: 'hidden',
                    maxHeight: isGroupOpen('my-menu') ? 500 : 0,
                    opacity: isGroupOpen('my-menu') ? 1 : 0,
                    transition: 'max-height 0.25s ease, opacity 0.2s ease',
                  }}
                >
                  {smartMenu.pinned.length > 0 && (
                    <>
                      <SmartDivider label="pinned" />
                      {smartMenu.pinned.map((item) => (
                        <NavItem
                          key={`pin-${item.key}`}
                          {...item}
                          collapsed={false}
                          pinned
                          onTogglePin={() => togglePin(item.key)}
                        />
                      ))}
                    </>
                  )}
                  {smartMenu.mostUsed.length > 0 && (
                    <>
                      <SmartDivider label="frequent" />
                      {smartMenu.mostUsed.map((item) => (
                        <NavItem
                          key={`freq-${item.key}`}
                          {...item}
                          collapsed={false}
                          onTogglePin={() => togglePin(item.key)}
                          pinned={isPinned(item.key)}
                        />
                      ))}
                    </>
                  )}
                  {smartMenu.recent.length > 0 && (
                    <>
                      <SmartDivider label="recent" />
                      {smartMenu.recent.map((item) => (
                        <NavItem
                          key={`rec-${item.key}`}
                          {...item}
                          collapsed={false}
                          onTogglePin={() => togglePin(item.key)}
                          pinned={isPinned(item.key)}
                        />
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}

            {NAV_GROUPS.map((group, gi) => {
              const open = isGroupOpen(group.key);
              const dividerStyle =
                gi > 0
                  ? { marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }
                  : {};

              // ── Sub-grouped section (tab switcher) ──
              if (group.subGroups) {
                // In collapsed mode: show all items from every sub-group, separated by a thin rule
                if (collapsed) {
                  const allVisible = group.subGroups.flatMap((sg, sgi) => {
                    const items = filterItems(sg.items);
                    if (items.length === 0) return [];
                    return sgi === 0 ? items : [{ __divider: true, key: sg.key }, ...items];
                  });
                  if (allVisible.length === 0) return null;
                  return (
                    <div key={group.key} style={dividerStyle}>
                      {allVisible.map((item) =>
                        item.__divider ? (
                          <div
                            key={`div-${item.key}`}
                            style={{
                              height: 1,
                              background: 'rgba(255,255,255,0.05)',
                              margin: '3px 8px',
                            }}
                          />
                        ) : (
                          <NavItem key={item.path} {...item} collapsed={true} />
                        )
                      )}
                    </div>
                  );
                }

                // In expanded mode: tab strip + items for active sub-group
                const activeKey = getActiveSubGroupKey(group);
                const activeSg =
                  group.subGroups.find((sg) => sg.key === activeKey) || group.subGroups[0];
                const visibleItems = filterItems(activeSg.items);
                // Only render the group if at least one sub-group has visible items
                const anyVisible = group.subGroups.some((sg) => filterItems(sg.items).length > 0);
                if (!anyVisible) return null;

                return (
                  <div key={group.key} style={dividerStyle}>
                    <GroupLabel
                      label={group.label}
                      collapsed={false}
                      isOpen={open}
                      onToggle={() => toggleGroup(group.key)}
                    />
                    <div
                      style={{
                        overflow: 'hidden',
                        maxHeight: open ? 600 : 0,
                        opacity: open ? 1 : 0,
                        transition: 'max-height 0.25s ease, opacity 0.2s ease',
                      }}
                    >
                      <SubGroupTabs
                        subGroups={group.subGroups}
                        activeKey={activeKey}
                        onSelect={(key) =>
                          setActiveSubGroups((prev) => ({ ...prev, [group.key]: key }))
                        }
                      />
                      {visibleItems.map((item) => (
                        <NavItem
                          key={item.path}
                          {...item}
                          collapsed={false}
                          onTogglePin={() => togglePin(item.key)}
                          pinned={isPinned(item.key)}
                        />
                      ))}
                    </div>
                  </div>
                );
              }

              // ── Flat section (original behaviour) ──
              const visibleItems = filterItems(group.items);
              if (visibleItems.length === 0) return null;
              return (
                <div key={group.key} style={dividerStyle}>
                  <GroupLabel
                    label={group.label}
                    collapsed={collapsed}
                    isOpen={open}
                    onToggle={() => toggleGroup(group.key)}
                  />
                  <div
                    style={{
                      overflow: 'hidden',
                      maxHeight: collapsed || open ? visibleItems.length * 40 : 0,
                      opacity: collapsed || open ? 1 : 0,
                      transition: 'max-height 0.25s ease, opacity 0.2s ease',
                    }}
                  >
                    {visibleItems.map((item) => (
                      <NavItem
                        key={item.path}
                        {...item}
                        collapsed={collapsed}
                        onTogglePin={!collapsed ? () => togglePin(item.key) : undefined}
                        pinned={isPinned(item.key)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* System */}
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              <GroupLabel
                label="System"
                collapsed={collapsed}
                isOpen={isGroupOpen('system')}
                onToggle={() => toggleGroup('system')}
              />
              <div
                style={{
                  overflow: 'hidden',
                  maxHeight: collapsed || isGroupOpen('system') ? 200 : 0,
                  opacity: collapsed || isGroupOpen('system') ? 1 : 0,
                  transition: 'max-height 0.25s ease, opacity 0.2s ease',
                }}
              >
                {SYSTEM_NAV.filter((item) =>
                  navRoleAllowed(item, { canAdmin: canAdminProp, role, msrPublish, releasedFlags })
                )
                  .filter((item) => {
                    const isUnlicensed =
                      planGating && !planGating.isModuleAllowed(navModuleKey(item));
                    return !isUnlicensed || showUnlicensed;
                  })
                  .map((item) => {
                    const isUnlicensed =
                      planGating && !planGating.isModuleAllowed(navModuleKey(item));
                    return isUnlicensed ? { ...item, unlicensed: true } : item;
                  })
                  .map((item) => (
                    <NavItem
                      key={item.path}
                      {...item}
                      small
                      collapsed={collapsed}
                      onTogglePin={!collapsed ? () => togglePin(item.key) : undefined}
                      pinned={isPinned(item.key)}
                    />
                  ))}
                <NavItem
                  path="/settings"
                  icon="ᛋ"
                  label="Settings"
                  small
                  collapsed={collapsed}
                  onTogglePin={!collapsed ? () => togglePin('settings') : undefined}
                  pinned={isPinned('settings')}
                />
              </div>
            </div>
          </>
        )}
      </nav>

      {/* ── Collapse / Expand toggle ── */}
      <div
        style={{
          padding: collapsed ? '8px 0' : '8px 10px',
          borderTop: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-end',
        }}
      >
        <button
          onClick={() => handleCollapse(!collapsed)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            padding: '5px 9px',
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--fg3)',
            cursor: 'pointer',
            lineHeight: 1,
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(45,212,180,0.3)';
            e.currentTarget.style.color = 'var(--accent)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.color = 'var(--fg3)';
          }}
        >
          {collapsed ? '›' : '‹'}
        </button>
      </div>

      {/* ── User card ── */}
      {collapsed ? (
        <div
          style={{
            padding: '12px 0 14px',
            borderTop: '1px solid var(--border)',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <button
            onClick={() => pushModal({ type: 'action-centre' })}
            title="Action Centre"
            style={{
              position: 'relative',
              padding: '6px 8px',
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              borderRadius: 7,
              fontSize: 16,
              color: hasDocumentApprovals ? '#facc15' : 'var(--fg3)',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ◎
            {actionBadge && (
              <span
                title={actionBadgeTitle}
                style={{
                  position: 'absolute',
                  top: -5,
                  right: -5,
                  minWidth: 16,
                  height: 16,
                  borderRadius: 8,
                  background: hasDocumentApprovals ? '#facc15' : 'var(--accent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#0d1f1e',
                  padding: '0 3px',
                }}
              >
                {prospect ? '9+' : actionBadge}
              </span>
            )}
          </button>
        </div>
      ) : (
        <div
          style={{ padding: '12px 10px 14px', borderTop: '1px solid var(--border)', flexShrink: 0 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--fg)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {session?.displayName || '—'}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--fg3)',
                  fontFamily: 'var(--font-mono)',
                  marginTop: 1,
                }}
              >
                {role}
              </div>
            </div>
            <button
              onClick={() => pushModal({ type: 'action-centre' })}
              title="Action Centre"
              style={{
                position: 'relative',
                padding: '6px 8px',
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 7,
                fontSize: 12,
                color: hasDocumentApprovals ? '#facc15' : 'var(--fg3)',
                cursor: 'pointer',
                flexShrink: 0,
                lineHeight: 1,
                transition: 'color 0.15s',
              }}
            >
              ◎
              {actionBadge && (
                <span
                  title={actionBadgeTitle}
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    background: hasDocumentApprovals ? '#facc15' : 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#0d1f1e',
                    padding: '0 3px',
                  }}
                >
                  {prospect ? '9+' : actionBadge}
                </span>
              )}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <button
              onClick={onUserSettings}
              style={{
                flex: 1,
                padding: '6px 0',
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 10,
                color: 'var(--fg3)',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
              }}
            >
              settings
            </button>
            <button
              onClick={onLogout}
              style={{
                flex: 1,
                padding: '6px 0',
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 10,
                color: 'var(--fg3)',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
              }}
            >
              logout
            </button>
            <LayoutToggle currentStyle={sidebarStyle} onSwitch={handleSetSidebarStyle} />
          </div>

          <button
            onClick={() => navigate('/support')}
            title="Ask Tryggr support"
            style={{
              marginTop: 8,
              width: '100%',
              padding: '7px 10px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              color: 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(251,113,133,0.4)';
              e.currentTarget.style.color = '#fb7185';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.color = 'var(--fg3)';
            }}
          >
            <span style={{ fontSize: 13, lineHeight: 1 }}>&#9888;</span>
            Contact support
          </button>

          <div style={{ marginTop: 10, display: 'flex', gap: 4 }}>
            <button
              onClick={() => openChangelog()}
              title="View changelog"
              style={{
                flex: 1,
                padding: '5px 8px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(45,212,180,0.3)';
                e.currentTarget.style.color = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.color = '';
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  color: 'var(--fg3)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.05em',
                }}
              >
                VERSION
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 600,
                  color: 'var(--fg3)',
                  transition: 'color 0.2s',
                }}
              >
                v{displayVersion}
              </span>
            </button>
            {updateChecked && hasUpdates && (
              <button
                onClick={() => openChangelog()}
                title={`${pendingCount} update${pendingCount !== 1 ? 's' : ''} incoming — view changelog`}
                style={{
                  padding: '5px 8px',
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 9,
                  fontFamily: 'var(--font-mono)',
                  color: updateEnv === 'test' ? '#fbbf24' : 'var(--accent)',
                  whiteSpace: 'nowrap',
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: 'currentColor',
                    display: 'inline-block',
                    animation: 'sidebarPulse 2s ease-in-out infinite',
                  }}
                />
                {pendingCount} new
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Resize handle ── */}
      {!collapsed && (
        <div
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 5,
            cursor: 'col-resize',
            background: 'transparent',
            zIndex: 10,
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(45,212,180,0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        />
      )}
    </aside>
  );
}
